import { useId, useRef, type ChangeEvent } from 'react';
import { t } from '@/i18n';
import styles from './PhotoUpload.module.css';

interface PhotoUploadProps {
  label?: string;
  previewUrl: string | null;
  onFile: (file: File | null) => void;
  helperText?: string;
}

export function PhotoUpload({
  label = t.photoUpload.label,
  previewUrl,
  onFile,
  helperText = t.photoUpload.helper,
}: PhotoUploadProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    onFile(file);
  };

  const clear = () => {
    onFile(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div>
      {label ? <div className={styles.phlbl}>{label}</div> : null}
      <label
        htmlFor={inputId}
        className={[styles.phbox, previewUrl ? styles.hasFile : ''].filter(Boolean).join(' ')}
      >
        {previewUrl ? (
          <>
            <img className={styles.prev} src={previewUrl} alt="" />
            <button
              type="button"
              className={styles.phrm}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clear();
              }}
              aria-label={t.photoUpload.removeAria}
            >
              ×
            </button>
          </>
        ) : (
          <>
            <span className={styles.phico} aria-hidden>
              {String.fromCodePoint(0x1f4f7)}
            </span>
            <span className={styles.phtxt}>{t.photoUpload.tapToUpload}</span>
            <span className={styles.phsub}>{helperText}</span>
          </>
        )}
      </label>
      <input
        ref={inputRef}
        id={inputId}
        className={styles.hiddenInput}
        type="file"
        accept="image/*"
        onChange={onChange}
      />
    </div>
  );
}
